<template>
  <div>
    <b-alert
      v-if="partialError"
      variant="warning"
      show
      class="mb-1"
    >
      <div class="alert-body">
        {{ partialError }}
      </div>
    </b-alert>

    <b-alert
      v-if="loadError"
      variant="danger"
      show
      class="mb-1"
    >
      <div class="alert-body">
        {{ loadError }}
      </div>
    </b-alert>

    <!-- ตัวกรองช่วงวัน -->
    <b-card class="mb-1">
      <b-row>
        <b-col
          cols="12"
          sm="6"
          md="3"
        >
          <b-form-group
            label="ช่วงเวลา"
            label-for="preset"
          >
            <b-form-select
              id="preset"
              v-model="preset"
              :options="presetOptions"
              :disabled="isLoading"
              @change="onPresetChange"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          sm="6"
          md="3"
        >
          <b-form-group
            label="ตั้งแต่"
            label-for="from"
          >
            <b-form-input
              id="from"
              v-model="from"
              type="date"
              :disabled="isLoading"
              @change="onDateChange"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          sm="6"
          md="3"
        >
          <b-form-group
            label="ถึง"
            label-for="to"
          >
            <b-form-input
              id="to"
              v-model="to"
              type="date"
              :disabled="isLoading"
              @change="onDateChange"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          sm="6"
          md="3"
          class="d-flex align-items-center mb-1 mb-md-0"
        >
          <b-button
            variant="outline-primary"
            :disabled="isLoading"
            @click="fetchData"
          >
            <b-spinner
              v-if="isLoading"
              small
              class="mr-50"
            />
            รีเฟรช
          </b-button>
          <b-badge
            v-if="cached"
            variant="light-secondary"
            class="ml-1"
          >
            จาก cache
          </b-badge>
        </b-col>
        <b-col md="12">
          <!--
            ค่าเริ่มต้นตัด bot ออก เพราะ bot ราว 14% ของ session ทำให้ทุกอัตราส่วนถูกเจือจาง
            เปิดดูแบบรวมได้เวลาอยากรู้ว่าโดน crawl หนักแค่ไหน
          -->
          <b-form-checkbox
            v-model="includeBots"
            switch
            :disabled="isLoading"
            @change="fetchData"
          >
            รวม bot ในรายงาน
            <small class="text-muted">(ค่าเริ่มต้นตัดออก — bot ทำให้อัตราส่วนเพี้ยน)</small>
          </b-form-checkbox>
        </b-col>
      </b-row>
    </b-card>

    <!--
      ยังไม่มีข้อมูล = ปกติสำหรับช่วงแรก ไม่ใช่หน้าพัง
      บอกให้ชัดว่าต้องทำอะไรต่อ ไม่งั้นคนอ่านจะคิดว่ารายงานเสีย
    -->
    <b-alert
      v-if="!isLoading && isEmpty"
      variant="info"
      show
      class="mb-1"
    >
      <div class="alert-body">
        <strong>ยังไม่มีข้อมูลในช่วงที่เลือก</strong>
        <div class="mt-25">
          ข้อมูลจะเริ่มเข้าเมื่อ SDK ถูก deploy ขึ้นเว็บหน้าบ้านแล้วมีผู้ใช้เข้าชม
          ถ้า deploy แล้วแต่ยังว่าง ให้ตรวจว่า <code>/internal-api/analytics/batch</code> ตอบ 202
        </div>
      </div>
    </b-alert>

    <!-- ตัวเลขหลัก -->
    <b-row class="mb-1">
      <b-col
        cols="6"
        md="3"
      >
        <b-card class="text-center mb-1">
          <h4 class="mb-0">
            {{ number(summary.sessions) }}
          </h4>
          <small class="text-muted">Session</small>
        </b-card>
      </b-col>
      <b-col
        cols="6"
        md="3"
      >
        <b-card class="text-center mb-1">
          <h4 class="mb-0 text-success">
            {{ number(totalConversions) }}
          </h4>
          <small class="text-muted">การติดต่อทั้งหมด</small>
        </b-card>
      </b-col>
      <b-col
        cols="6"
        md="3"
      >
        <b-card class="text-center mb-1">
          <h4 class="mb-0">
            {{ percent(conversionRate) }}
          </h4>
          <small class="text-muted">อัตราติดต่อ / session</small>
        </b-card>
      </b-col>
      <b-col
        cols="6"
        md="3"
      >
        <b-card class="text-center mb-1">
          <h4
            class="mb-0"
            :class="bounceClass"
          >
            {{ percent(summary.bounce_rate) }}
          </h4>
          <small class="text-muted">Bounce rate</small>
        </b-card>
      </b-col>
    </b-row>

    <!--
      แนวโน้มผู้เข้าชมรายวัน — ดูว่างบ ads ที่ลงไปทำให้คนเพิ่มขึ้นจริงไหม
      และช่วยจับว่าวันไหนตก (แคมเปญหมดงบ / เว็บล่ม) โดยไม่ต้องไล่อ่านตัวเลขทีละแถว
    -->
    <b-card
      v-if="dauSeries[0].data.length"
      title="ผู้เข้าชมรายวัน"
      class="mb-1"
    >
      <vue-apex-charts
        type="area"
        height="250"
        :options="dauChartOptions"
        :series="dauSeries"
      />
    </b-card>

    <b-row>
      <!--
        Conversion แยกตามช่องทาง — ตัวเลขที่ใช้ตัดสินใจยิง ads โดยตรง
        ช่องทางไหนได้ lead เยอะ = ควรดันปุ่มนั้นให้เด่นและใช้เป็น optimisation goal
      -->
      <b-col md="6">
        <b-card
          title="ช่องทางที่ลูกค้าติดต่อเข้ามา"
          class="mb-1"
        >
          <vue-apex-charts
            v-if="conversionSeries.length"
            type="donut"
            height="260"
            :options="donutOptions(conversionLabels)"
            :series="conversionSeries"
          />
          <b-table
            :items="conversions"
            :fields="conversionFields"
            :busy="dashLoading"
            show-empty
            empty-text="ยังไม่มีการติดต่อในช่วงนี้"
            responsive
            striped
          >
            <template #cell(conversion_type)="row">
              <b-badge :variant="channelVariant(row.item.conversion_type)">
                {{ channelLabel(row.item.conversion_type) }}
              </b-badge>
            </template>
            <template #cell(share)="row">
              {{ percent(shareOf(row.item.cnt)) }}
            </template>
          </b-table>
        </b-card>
      </b-col>

      <!--
        Funnel: ค้นหา -> ดูห้อง -> ติดต่อ
        ขั้นที่ตัวเลขตกแรงที่สุดคือจุดที่ควรแก้ก่อน การเพิ่มงบ ads แก้ตรงนั้นไม่ได้
      -->
      <b-col md="6">
        <b-card
          title="เส้นทางจากการค้นหา"
          class="mb-1"
        >
          <div
            v-for="step in funnelSteps"
            :key="step.key"
            class="mb-1"
          >
            <div class="d-flex justify-content-between mb-25">
              <span>{{ step.label }}</span>
              <span>
                <strong>{{ number(step.value) }}</strong>
                <small
                  v-if="step.rate !== null"
                  class="text-muted ml-50"
                >
                  ({{ percent(step.rate) }} จากขั้นก่อน)
                </small>
              </span>
            </div>
            <b-progress
              :value="step.width"
              :max="100"
              height="8px"
              :variant="step.variant"
            />
          </div>
          <small
            v-if="!isEmpty"
            class="text-muted"
          >
            นับเป็น <strong>จำนวนการค้นหา</strong> ไม่ใช่จำนวน session — ทุกขั้นจึงเป็นส่วนย่อยของขั้นก่อนหน้าจริง
            <br>
            คนที่เข้าหน้าห้องตรงจาก Google แล้วกดติดต่อจะไม่อยู่ในนี้ (ไม่ได้เริ่มจากการค้นหา)
            ดูยอดติดต่อทั้งหมดที่การ์ดด้านบน
          </small>
        </b-card>
      </b-col>
    </b-row>

    <b-card
      no-body
      class="mb-1"
    >
      <!--
        9 แท็บ บนมือถือจะพับลงมาหลายบรรทัดจนกินพื้นที่ครึ่งจอ
        บังคับให้อยู่บรรทัดเดียวแล้วเลื่อนแนวนอนแทน (ดูรวดเดียวว่ามีแท็บอะไรบ้าง)
      -->
      <b-tabs
        card
        nav-wrapper-class="analytics-tabs-nav"
      >
        <b-tab
          title="ห้องที่ถูกดูมากสุด"
          active
        >
          <b-table
            :items="paged.top_properties.rows"
            :fields="propertyFields"
            :busy="dashLoading || !!pagedLoading.top_properties"
            show-empty
            empty-text="ไม่มีข้อมูล"
            responsive
            striped
          />
          <div
            v-if="pagedShowPager('top_properties')"
            class="d-flex justify-content-center align-items-center"
          >
            <b-button
              v-if="pagedHasMultiple('top_properties')"
              size="sm"
              variant="outline-secondary"
              :disabled="paged.top_properties.page <= 1 || isLoading"
              @click="goPagedPage('top_properties', paged.top_properties.page - 1)"
            >
              ก่อนหน้า
            </b-button>
            <span class="mx-1 text-muted">{{ pagedLabel('top_properties') }}</span>
            <b-button
              v-if="pagedHasMultiple('top_properties')"
              size="sm"
              variant="outline-secondary"
              :disabled="!pagedHasNext('top_properties') || isLoading"
              @click="goPagedPage('top_properties', paged.top_properties.page + 1)"
            >
              ถัดไป
            </b-button>
          </div>
        </b-tab>

        <b-tab title="คำค้นยอดนิยม">
          <vue-apex-charts
            v-if="keywordChart.series[0].data.length"
            type="bar"
            :height="barHeight(keywordChart.options.xaxis.categories.length)"
            :options="keywordChart.options"
            :series="keywordChart.series"
          />
          <b-table
            :items="paged.top_search_keywords.rows"
            :fields="keywordFields"
            :busy="dashLoading || !!pagedLoading.top_search_keywords"
            show-empty
            empty-text="ไม่มีข้อมูล"
            responsive
            striped
          />
          <div
            v-if="pagedShowPager('top_search_keywords')"
            class="d-flex justify-content-center align-items-center"
          >
            <b-button
              v-if="pagedHasMultiple('top_search_keywords')"
              size="sm"
              variant="outline-secondary"
              :disabled="paged.top_search_keywords.page <= 1 || isLoading"
              @click="goPagedPage('top_search_keywords', paged.top_search_keywords.page - 1)"
            >
              ก่อนหน้า
            </b-button>
            <span class="mx-1 text-muted">{{ pagedLabel('top_search_keywords') }}</span>
            <b-button
              v-if="pagedHasMultiple('top_search_keywords')"
              size="sm"
              variant="outline-secondary"
              :disabled="!pagedHasNext('top_search_keywords') || isLoading"
              @click="goPagedPage('top_search_keywords', paged.top_search_keywords.page + 1)"
            >
              ถัดไป
            </b-button>
          </div>
        </b-tab>

        <!--
          คำที่ค้นแล้วไม่เจอ = ความต้องการที่เรายังไม่มีของ
          ใช้ตัดสินใจว่าควรไปหาทรัพย์แถวไหนมาลง และห้ามเอาคำพวกนี้ไปยิง ads
          เพราะจ่ายค่าคลิกแล้วผู้ใช้เจอหน้าว่าง
        -->
        <b-tab title="ค้นแล้วไม่เจอ">
          <vue-apex-charts
            v-if="noResultChart.series[0].data.length"
            type="bar"
            :height="barHeight(noResultChart.options.xaxis.categories.length)"
            :options="noResultChart.options"
            :series="noResultChart.series"
          />
          <b-table
            :items="dashboard.search_no_result"
            :fields="keywordFields"
            :busy="dashLoading"
            show-empty
            empty-text="ไม่มีข้อมูล"
            responsive
            striped
          />
        </b-tab>

        <b-tab title="มาจากไหน">
          <vue-apex-charts
            v-if="sourceSeries.length"
            type="donut"
            height="280"
            :options="donutOptions(sourceLabels)"
            :series="sourceSeries"
          />
          <b-table
            :items="dashboard.traffic_source"
            :fields="breakdownFields"
            :busy="dashLoading"
            show-empty
            empty-text="ไม่มีข้อมูล"
            responsive
            striped
          />
        </b-tab>

        <!--
          ทางเข้าที่ทำให้คนเปิดดูห้อง — ส่วนไหนของเว็บทำงานจริง
          ดูคู่กับ lead เสมอ เพราะจำนวนคลิกอย่างเดียวหลอกได้: ส่วนที่วางไว้เด่น
          ย่อมได้คลิกเยอะเป็นธรรมดา แต่ถ้าไม่มีใครติดต่อเลยแปลว่าดึงคนผิดกลุ่ม
        -->
        <b-tab>
          <template #title>
            <span
              class="d-block"
              @click="ensureTab('viewSources')"
            >ทางเข้าที่คนดูห้อง</span>
          </template>
          <b-table
            :items="viewSources"
            :fields="viewSourceFields"
            :busy="lazy.viewSources.loading"
            show-empty
            empty-text="ยังไม่มีข้อมูล"
            responsive
            striped
          >
            <template #cell(view_source)="row">
              <b-badge :variant="sourceVariant(row.item.view_source)">
                {{ sourceLabel(row.item.view_source) }}
              </b-badge>
            </template>
            <template #cell(lead_rate)="row">
              {{ percent(rate(row.item.leads, row.item.views)) }}
            </template>
          </b-table>
          <small class="text-muted">
            <strong>เข้าตรง</strong> = มาจาก Google / ลิงก์ที่แชร์ / พิมพ์ URL เอง ไม่ได้กดผ่านหน้าไหนในเว็บ
          </small>
        </b-tab>

        <!--
          แคมเปญโฆษณา — คำถามปลายทางของงานนี้: งบที่ลงไปได้ lead กลับมาไหม
          ใช้ first-touch ต่อ visitor เพราะ conversion เกิดในแท็บใหม่ที่ไม่มี UTM ติดมา
        -->
        <b-tab>
          <template #title>
            <span
              class="d-block"
              @click="ensureTab('utm')"
            >แคมเปญ (UTM)</span>
          </template>
          <b-alert
            v-if="lazy.utm.loaded && !lazy.utm.loading && (!utm.length || utmIsAllDirect)"
            variant="warning"
            show
          >
            <div class="alert-body">
              <strong>ยังไม่มีข้อมูลแคมเปญ</strong>
              <div class="mt-25">
                ต้องใส่ UTM ในลิงก์โฆษณาก่อน เช่น
                <code>?utm_source=facebook&amp;utm_medium=cpc&amp;utm_campaign=condo-asoke</code>
                — ระบบเก็บให้เองอัตโนมัติ ไม่ต้องแก้โค้ด
              </div>
            </div>
          </b-alert>
          <b-table
            :items="utm"
            :fields="utmFields"
            :busy="lazy.utm.loading"
            show-empty
            empty-text="ไม่มีข้อมูล"
            responsive
            striped
          >
            <template #cell(lead_rate)="row">
              {{ percent(rate(row.item.leads, row.item.visitors)) }}
            </template>
            <template #cell(lead_value)="row">
              {{ money(row.item.lead_value) }}
            </template>
          </b-table>
        </b-tab>

        <!--
          พื้นที่ระดับจังหวัด/เมือง ไม่ใช่แค่ประเทศ — เว็บไทยถ้าดูแค่ประเทศจะได้ TH ก้อนเดียว
        -->
        <b-tab>
          <template #title>
            <span
              class="d-block"
              @click="ensureTab('geo')"
            >พื้นที่</span>
          </template>
          <b-table
            :items="geo"
            :fields="geoFields"
            :busy="lazy.geo.loading"
            show-empty
            empty-text="ไม่มีข้อมูล"
            responsive
            striped
          />
          <!-- ปุ่มก่อนหน้า/ถัดไป — API บอกแค่ว่ามีหน้าถัดไปไหม ไม่ได้นับจำนวนหน้าทั้งหมด -->
          <div
            v-if="showPager(geoPage, geoTotalPages, geoHasMore)"
            class="d-flex justify-content-center align-items-center mb-1"
          >
            <b-button
              v-if="hasMultiplePages(geoPage, geoTotalPages, geoHasMore)"
              size="sm"
              variant="outline-secondary"
              :disabled="geoPage <= 1 || isLoading"
              @click="goGeoPage(geoPage - 1)"
            >
              ก่อนหน้า
            </b-button>
            <span class="mx-1 text-muted">{{ pageLabel(geoPage, geoTotalPages, geoTotal) }}</span>
            <b-button
              v-if="hasMultiplePages(geoPage, geoTotalPages, geoHasMore)"
              size="sm"
              variant="outline-secondary"
              :disabled="!hasNextPage(geoPage, geoTotalPages, geoHasMore) || isLoading"
              @click="goGeoPage(geoPage + 1)"
            >
              ถัดไป
            </b-button>
          </div>
          <small class="text-muted">
            แถวที่ขึ้น "(ไม่ทราบ)" คือข้อมูลที่เก็บก่อนติดตั้ง geoip — ของใหม่จะระบุพื้นที่ได้
          </small>
        </b-tab>

        <b-tab title="อุปกรณ์">
          <vue-apex-charts
            v-if="deviceSeries.length"
            type="donut"
            height="280"
            :options="donutOptions(deviceLabels)"
            :series="deviceSeries"
          />
          <b-table
            :items="dashboard.device"
            :fields="breakdownFields"
            :busy="dashLoading"
            show-empty
            empty-text="ไม่มีข้อมูล"
            responsive
            striped
          />
        </b-tab>

        <b-tab title="หน้าที่เข้าชม">
          <b-table
            :items="paged.top_pages.rows"
            :fields="pageFields"
            :busy="dashLoading || !!pagedLoading.top_pages"
            show-empty
            empty-text="ไม่มีข้อมูล"
            responsive
            striped
          />
          <div
            v-if="pagedShowPager('top_pages')"
            class="d-flex justify-content-center align-items-center"
          >
            <b-button
              v-if="pagedHasMultiple('top_pages')"
              size="sm"
              variant="outline-secondary"
              :disabled="paged.top_pages.page <= 1 || isLoading"
              @click="goPagedPage('top_pages', paged.top_pages.page - 1)"
            >
              ก่อนหน้า
            </b-button>
            <span class="mx-1 text-muted">{{ pagedLabel('top_pages') }}</span>
            <b-button
              v-if="pagedHasMultiple('top_pages')"
              size="sm"
              variant="outline-secondary"
              :disabled="!pagedHasNext('top_pages') || isLoading"
              @click="goPagedPage('top_pages', paged.top_pages.page + 1)"
            >
              ถัดไป
            </b-button>
          </div>
        </b-tab>

        <b-tab title="ข้อผิดพลาด">
          <b-table
            :items="dashboard.errors"
            :fields="errorFields"
            :busy="dashLoading"
            show-empty
            empty-text="ไม่มีข้อผิดพลาด"
            responsive
            striped
          />
        </b-tab>
      </b-tabs>
    </b-card>

    <b-card title="ความเร็วหน้าเว็บ (Core Web Vitals)">
      <b-row>
        <b-col
          v-for="v in vitals"
          :key="v.key"
          md="2"
          cols="6"
          class="text-center mb-1"
        >
          <h5 class="mb-0">
            {{ v.value === null ? '—' : v.value }}
          </h5>
          <small class="text-muted">{{ v.label }}</small>
        </b-col>
      </b-row>
      <small class="text-muted">
        ค่าเฉลี่ย ไม่ใช่ p75 — ใช้ดูแนวโน้ม ไม่ใช่ใช้ตัดสินผ่าน/ไม่ผ่านเกณฑ์ Google
      </small>
    </b-card>
  </div>
</template>

<script>
/*
 * รายงานพฤติกรรมผู้ใช้เว็บหน้าบ้าน (ข้อมูลจาก api/analytics)
 *
 * อ่านจาก GET /analytics/v1/dashboard ซึ่งรวมทุกรายงานมาให้ในครั้งเดียว
 * (เรียกขนานกันฝั่ง server + cache ตาม config.cache.ttlSeconds)
 *
 * ทำไมถึงเน้น conversion กับ funnel เป็นหลัก: หน้านี้มีไว้ตัดสินใจเรื่องยิง ads
 * ตัวเลข session เยอะ ๆ ไม่ได้บอกว่าคุ้ม สิ่งที่บอกคือมีคนกดติดต่อกี่คน
 * และหล่นหายที่ขั้นไหน
 */
import {
  BCard, BTable, BBadge, BFormSelect, BFormGroup, BFormInput,
  BButton, BRow, BCol, BTabs, BTab, BAlert, BSpinner, BProgress, BFormCheckbox,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import {
  analyticsHttp, apiToken, settleOne, rowsOf, loadErrorMessage,
  pageLabel, hasNextPage, showPager, hasMultiplePages,
} from './analyticsApi'

/** โครงเปล่า ใช้ทั้งตอน init และตอนโหลดพัง เพื่อให้ template ไม่ต้องเช็ค null ทุกจุด */
/*
 * จำนวนแถวต่อหน้าของตารางที่แบ่งหน้า
 *
 * ⚠️ ต้องไม่เกิน DEFAULT_PER_PAGE ของ API (50) เพราะหน้าแรกตัดมาจากชุดที่
 * /dashboard ส่งมาให้ (50 แถว) ถ้าตั้งเกิน 50 หน้าแรกจะขาดแถวไปเงียบ ๆ
 */
const PAGED_PER_PAGE = 20

/** ชื่อแท็บสำหรับข้อความเตือนตอนโหลดไม่สำเร็จ */
const TAB_LABELS = {
  viewSources: 'ทางเข้าที่คนดูห้อง',
  geo: 'พื้นที่',
  utm: 'แคมเปญ (UTM)',
}

const emptyDashboard = () => ({
  summary: {},
  dau: [],
  top_pages: [],
  top_properties: [],
  top_keywords: [],
  search_no_result: [],
  buttons: [],
  conversions: [],
  funnel: {},
  performance: {},
  errors: [],
  traffic_source: [],
  device: [],
})

const CHANNEL_LABEL = {
  line: 'LINE',
  whatsapp: 'WhatsApp',
  phone: 'โทร',
  email: 'อีเมล',
  contact_form: 'ฟอร์มติดต่อ',
  contact: 'ติดต่อ',
  booking: 'จองชม',
  favorite: 'บันทึกไว้',
  share: 'แชร์',
  register: 'สมัครสมาชิก',
  login: 'เข้าสู่ระบบ',
}

const iso = d => d.toISOString().slice(0, 10)

/*
 * จานสีของกราฟ — ล็อกไว้เองแทนที่จะใช้ค่า default ของ apex
 *
 * เพราะ donut ให้สีตามลำดับข้อมูล ถ้าปล่อยอัตโนมัติ "LINE" จะเปลี่ยนสีทุกครั้งที่
 * อันดับสลับ คนอ่านรายงานประจำจะจำสีไม่ได้
 */
const PALETTE = ['#28c76f', '#7367f0', '#00cfe8', '#ff9f43', '#ea5455', '#9600ff', '#1e9ff2', '#b8c2cc']

const APEX_BASE = {
  chart: { toolbar: { show: false }, fontFamily: 'inherit' },
  dataLabels: { enabled: false },
  colors: PALETTE,

  /*
   * บนจอแคบ legend ด้านข้างจะบีบโดนัทจนเล็กจนอ่านไม่ออก และ % บนชิ้นส่วนจะทับกัน
   * ย้าย legend ลงล่างและปิด dataLabels เมื่อจอเล็กกว่า 576px
   */
  responsive: [
    {
      breakpoint: 576,
      options: {
        legend: { position: 'bottom', fontSize: '11px', itemMargin: { horizontal: 4 } },
        dataLabels: { enabled: false },
        plotOptions: { pie: { donut: { size: '60%' } } },
      },
    },
  ],
}

export default {
  components: {
    BCard,
    BTable,
    BBadge,
    BFormSelect,
    BFormGroup,
    BFormInput,
    BButton,
    BRow,
    BCol,
    BTabs,
    BTab,
    BAlert,
    BSpinner,
    BProgress,
    BFormCheckbox,
    VueApexCharts,
  },
  data() {
    const today = new Date()
    /* 7 วันล่าสุดนับรวมวันนี้ จึงลบ 6 (เปลี่ยนจาก 30 วันเมื่อ 21 ก.ย. 2026) */
    const past = new Date(today.getTime() - 6 * 86400000)

    return {
      dashboard: emptyDashboard(),
      utm: [],
      geo: [],
      viewSources: [],

      /* /dashboard ชุดใหญ่ยังโหลดอยู่ไหม — แยกจาก isLoading ที่คุมแค่ตัวเลขหัวตาราง */
      dashLoading: false,
      /* ตารางที่แบ่งหน้า: กำลังโหลดหน้าใหม่อยู่ไหม (หน้าแรกมากับ /dashboard) */
      pagedLoading: {},
      /*
       * แท็บที่ยิงแยกและช้าที่สุดสามตัว — ยังไม่โหลดจนกว่าจะกดเข้าไปดู
       * loaded กันไม่ให้ยิงซ้ำตอนสลับแท็บไปมา
       */
      lazy: {
        viewSources: { loaded: false, loading: false, failed: false },
        geo: { loaded: false, loading: false, failed: false },
        utm: { loaded: false, loading: false, failed: false },
      },

      /*
       * ตารางที่ข้อมูลโตไม่จำกัด ต้องแบ่งหน้าจาก server
       *
       * เดิมอ่านจากชุด /dashboard ซึ่งคืนตายตัว 50 แถวและไม่มีทางดูส่วนที่เหลือ
       * ตรวจกับข้อมูลจริงแล้วขาดไปเยอะ: หน้าที่เข้าชม 550 แถวเห็นแค่ 50,
       * ห้องที่ถูกดู 226 แถว, คำค้น 102 แถว
       */
      paged: {
        top_properties: {
          rows: [], page: 1, hasMore: false, total: null, totalPages: null,
        },
        top_search_keywords: {
          rows: [], page: 1, hasMore: false, total: null, totalPages: null,
        },
        top_pages: {
          rows: [], page: 1, hasMore: false, total: null, totalPages: null,
        },
      },
      geoPage: 1,
      geoHasMore: false,
      geoTotal: null,
      geoTotalPages: null,
      partialError: '',
      cached: false,
      isLoading: false,
      loadError: '',
      includeBots: false,
      preset: 7,
      from: iso(past),
      to: iso(today),
      presetOptions: [
        { value: 7, text: '7 วันล่าสุด' },
        { value: 30, text: '30 วันล่าสุด' },
        { value: 90, text: '90 วันล่าสุด' },
        { value: 0, text: 'กำหนดเอง' },
      ],
      conversionFields: [
        { key: 'conversion_type', label: 'ช่องทาง' },
        { key: 'cnt', label: 'ครั้ง', class: 'text-center' },
        { key: 'visitors', label: 'คน', class: 'text-center' },
        { key: 'share', label: 'สัดส่วน', class: 'text-center' },
      ],
      propertyFields: [
        { key: 'property_id', label: 'Property ID' },
        { key: 'project', label: 'โครงการ' },
        { key: 'views', label: 'ครั้งที่ดู', class: 'text-center' },
      ],
      keywordFields: [
        { key: 'keyword', label: 'คำค้น' },
        { key: 'cnt', label: 'ครั้ง', class: 'text-center' },
      ],
      breakdownFields: [
        { key: 'label', label: 'ที่มา' },
        { key: 'sessions', label: 'Session', class: 'text-center' },
        { key: 'visitors', label: 'คน', class: 'text-center' },
      ],
      pageFields: [
        { key: 'page_url', label: 'หน้า' },
        { key: 'views', label: 'เข้าชม', class: 'text-center' },
        { key: 'avg_seconds', label: 'อยู่นาน (วิ)', class: 'text-center' },
      ],
      viewSourceFields: [
        { key: 'view_source', label: 'ทางเข้า' },
        { key: 'views', label: 'เปิดดู', class: 'text-center' },
        { key: 'visitors', label: 'คน', class: 'text-center' },
        { key: 'leads', label: 'ติดต่อ', class: 'text-center' },
        { key: 'lead_rate', label: 'ติดต่อ/ดู', class: 'text-center' },
        { key: 'avg_position', label: 'อันดับเฉลี่ย', class: 'text-center' },
      ],
      utmFields: [
        { key: 'source', label: 'ที่มา' },
        { key: 'medium', label: 'ประเภท' },
        { key: 'campaign', label: 'แคมเปญ' },
        { key: 'visitors', label: 'คน', class: 'text-center' },
        { key: 'sessions', label: 'Session', class: 'text-center' },
        { key: 'leads', label: 'ติดต่อ', class: 'text-center' },
        { key: 'lead_rate', label: 'ติดต่อ/คน', class: 'text-center' },
        { key: 'lead_value', label: 'มูลค่า', class: 'text-right' },
      ],
      geoFields: [
        { key: 'country', label: 'ประเทศ' },
        { key: 'province', label: 'จังหวัด' },
        { key: 'city', label: 'เมือง' },
        { key: 'sessions', label: 'Session', class: 'text-center' },
        { key: 'visitors', label: 'คน', class: 'text-center' },
        { key: 'leads', label: 'ติดต่อ', class: 'text-center' },
      ],
      errorFields: [
        { key: 'event_name', label: 'ประเภท' },
        { key: 'message', label: 'ข้อความ' },
        { key: 'cnt', label: 'ครั้ง', class: 'text-center' },
      ],
    }
  },
  computed: {
    summary() {
      return this.dashboard.summary || {}
    },
    conversions() {
      return this.dashboard.conversions || []
    },
    totalConversions() {
      return this.conversions.reduce((sum, r) => sum + Number(r.cnt || 0), 0)
    },
    conversionRate() {
      const s = Number(this.summary.sessions || 0)

      return s ? (100 * this.totalConversions) / s : null
    },
    bounceClass() {
      const b = Number(this.summary.bounce_rate)

      if (!Number.isFinite(b)) return ''

      return b > 70 ? 'text-danger' : 'text-body'
    },

    /*
     * ความกว้างของแท่งวัดเทียบกับขั้นแรกเสมอ (ไม่ใช่เทียบขั้นก่อนหน้า)
     * เพื่อให้เห็นภาพว่าเหลือรอดกี่ % จากต้นทาง ส่วนตัวเลข % ข้าง ๆ เทียบขั้นก่อนหน้า
     * ซึ่งเป็นตัวที่ชี้ว่าคอขวดอยู่ตรงไหน
     */
    funnelSteps() {
      const f = this.dashboard.funnel || {}
      const searched = Number(f.searched || 0)
      const viewed = Number(f.viewed_property || 0)
      const converted = Number(f.converted || 0)
      const base = searched || viewed || converted || 0
      const width = v => (base ? Math.round((100 * v) / base) : 0)
      const rate = (v, prev) => (prev ? (100 * v) / prev : null)

      return [
        {
          key: 'searched', label: 'ค้นหา', value: searched, width: width(searched), rate: null, variant: 'primary',
        },
        {
          key: 'viewed', label: 'เปิดดูห้อง', value: viewed, width: width(viewed), rate: rate(viewed, searched), variant: 'info',
        },
        {
          key: 'converted', label: 'กดติดต่อ', value: converted, width: width(converted), rate: rate(converted, viewed), variant: 'success',
        },
      ]
    },
    vitals() {
      const p = this.dashboard.performance || {}
      const pick = k => (p[k] === null || p[k] === undefined ? null : p[k])

      return [
        { key: 'lcp', label: 'LCP (ms)', value: pick('avg_lcp') },
        { key: 'inp', label: 'INP (ms)', value: pick('avg_inp') },
        { key: 'cls', label: 'CLS', value: pick('avg_cls') },
        { key: 'ttfb', label: 'TTFB (ms)', value: pick('avg_ttfb') },
        { key: 'fcp', label: 'FCP (ms)', value: pick('avg_fcp') },
        { key: 'load', label: 'โหลดเสร็จ (ms)', value: pick('avg_page_load') },
      ]
    },
    isEmpty() {
      return !Number(this.summary.sessions || 0) && !this.totalConversions
    },

    pagedKeys() {
      return Object.keys(this.paged)
    },

    /** ทุกแถวเป็น (direct) = ยังไม่มีใครติด UTM ในลิงก์เลย */
    utmIsAllDirect() {
      return this.utm.length > 0 && this.utm.every(r => r.source === '(direct)')
    },

    // ── กราฟ ────────────────────────────────────────────────────────────────

    dauSeries() {
      const rows = this.dashboard.dau || []

      return [{
        name: 'ผู้เข้าชม',
        data: rows.map(r => ({ x: r.d, y: Number(r.dau || 0) })),
      }]
    },
    dauChartOptions() {
      return {
        ...APEX_BASE,
        chart: { ...APEX_BASE.chart, type: 'area', zoom: { enabled: false } },
        colors: ['#7367f0'],
        stroke: { curve: 'smooth', width: 2 },
        fill: { type: 'gradient', gradient: { shadeIntensity: 0.2, opacityFrom: 0.5, opacityTo: 0.05 } },

        /*
         * datetime ไม่ใช่ category — ถ้าใช้ category วันที่ที่ไม่มีข้อมูลจะหายไปจากแกน
         * ทำให้กราฟดูต่อเนื่องทั้งที่จริงมีวันที่คนเข้าเป็นศูนย์
         */
        xaxis: { type: 'datetime', labels: { format: 'd MMM' } },
        yaxis: { labels: { formatter: v => Math.round(v) } },
        tooltip: { x: { format: 'd MMM yyyy' } },
        grid: { borderColor: 'rgba(0,0,0,.08)' },
      }
    },

    conversionLabels() {
      return this.conversions.map(r => this.channelLabel(r.conversion_type))
    },
    conversionSeries() {
      return this.conversions.map(r => Number(r.cnt || 0))
    },

    sourceLabels() {
      return (this.dashboard.traffic_source || []).slice(0, 8).map(r => r.label)
    },
    sourceSeries() {
      return (this.dashboard.traffic_source || []).slice(0, 8).map(r => Number(r.sessions || 0))
    },

    deviceLabels() {
      return (this.dashboard.device || []).map(r => r.label)
    },
    deviceSeries() {
      return (this.dashboard.device || []).map(r => Number(r.sessions || 0))
    },

    keywordChart() {
      return this.barChart(this.paged.top_search_keywords.rows, 'keyword', 'ครั้งที่ค้น', '#7367f0')
    },
    noResultChart() {
      return this.barChart(this.dashboard.search_no_result, 'keyword', 'ครั้งที่ค้นแล้วไม่เจอ', '#ea5455')
    },
  },
  created() {
    this.fetchData()
  },
  methods: {
    /*
     * ⚠️ ต้องแนบ token ของ API เอง ไม่ใช่ accessToken ใน localStorage
     *
     * accessToken ถูกเซ็นฝั่ง browser โดย @fake-db ด้วย secret คนละตัวกับ JWT_SECRET
     * ของ API ส่วน /analytics/v1/* ตรวจ jwt.verify จริง ถ้าปล่อยให้ interceptor
     * แนบ accessToken ไปจะได้ 403 ทุกครั้ง
     *
     * token ที่ API ยอมรับคือ userData.token ซึ่ง /users/v1/login คืนมาตอนล็อกอิน
     * และถูกเก็บติดมากับ userData อยู่แล้ว
     */

    /*
     * ⚠️ ต้องต่อเวลาท้ายวันเอง — API เทียบด้วย BETWEEN ตรง ๆ
     *
     * ถ้าส่งแค่ 'to=2026-08-21' MySQL อ่านเป็น '2026-08-21 00:00:00' ข้อมูลทั้งวันนั้น
     * จึงหลุดออกจากรายงาน ซึ่งแปลว่าวันนี้จะไม่มีวันแสดงผลเลย (ทดสอบแล้ว: 0 แถว
     * เทียบกับ 171 แถวเมื่อต่อ 23:59:59)
     */
    /*
     * ── ตัวช่วยประกอบ request ────────────────────────────────────────────────
     * ทุก request ใช้ช่วงวัน + token ชุดเดียวกัน แยกออกมาเพื่อไม่ต้องเขียนซ้ำ
     * ในแต่ละ fetch* และเพื่อให้ "ช่วงวันที่ยิงไป" ตรงกันทั้งหน้าเสมอ
     */
    requestBase() {
      const token = apiToken()

      if (!token) return null

      return {
        params: {
          from: `${this.from} 00:00:00`,
          to: `${this.to} 23:59:59`,
          ...(this.includeBots ? { include_bots: '1' } : {}),
        },
        headers: { Authorization: `Bearer ${token}` },
      }
    },

    /*
     * ⚠️ with_total=0 ทุก request ที่เป็นลิสต์
     *
     * COUNT ฝั่ง API = รัน GROUP BY ทั้งก้อนซ้ำอีกรอบ (top_pages LIST 6.5s + COUNT 6.7s)
     * หน้านี้ไม่ได้ใช้เลข "จาก Y หน้า" ทำอะไรนอกจากแสดง ปุ่มก่อนหน้า/ถัดไปทำงานได้
     * ด้วย has_more อยู่แล้ว — แลกเลขหน้ารวมกับการตัดภาระ DB ครึ่งหนึ่ง
     */
    reportRequest(type, extra = {}) {
      const base = this.requestBase()

      /* กดเข้าแท็บ/เปลี่ยนหน้าได้แม้ token หาย — ต้องไม่ throw จนหน้าค้าง */
      if (!base) return Promise.reject(new Error('ไม่พบ token ของ API ใน userData'))

      return analyticsHttp.get('analytics/v1/report', {
        params: {
          ...base.params, type, with_total: '0', ...extra,
        },
        headers: base.headers,
      })
    },

    /*
     * ── การโหลดของหน้านี้แบ่งเป็น 3 จังหวะ ───────────────────────────────────
     *
     * 1. session_summary อย่างเดียว (วัดได้ 522ms) -> ตัวเลขหัวตารางขึ้นทันที
     * 2. /dashboard ชุดใหญ่ (13 query ขนานกัน ~6s) -> กราฟและตารางที่เหลือ
     * 3. แท็บที่ยังไม่เปิด -> ยังไม่ยิงเลย รอจนกดเข้าไปดูจริง
     *
     * เดิมรวมทุกอย่างไว้ใน await เดียว หน้าจึงว่างจนกว่าตัวช้าสุดจะเสร็จ
     * (geo_detail 22 วินาที ทั้งที่อยู่ในแท็บที่ยังไม่มีใครเปิด)
     */
    async fetchData() {
      if (!this.requestBase()) {
        this.dashboard = emptyDashboard()
        this.loadError = 'ไม่พบ token ของ API ใน userData — กรุณาออกจากระบบแล้วเข้าใหม่'

        return
      }

      this.loadError = ''
      this.partialError = ''

      /* ยิงขนานกัน แต่แต่ละตัวเขียนผลลง state เองทันทีที่มาถึง ไม่รอกัน */
      await Promise.all([
        this.fetchSummary(),
        this.fetchDashboard(),
        this.refreshLoadedTabs(),
      ])
    },

    /* ตัวเลขหัวตาราง — เบาที่สุดในหน้า ยิงแยกเพื่อให้หน้ามีอะไรให้ดูใน ~0.5 วินาที */
    async fetchSummary() {
      try {
        this.isLoading = true

        const r = await settleOne(this.reportRequest('session_summary'))

        /* ปล่อยให้ /dashboard เขียนทับทีหลัง ตัวนี้แค่มาก่อนเพื่อไม่ให้หน้าว่าง */
        if (r.ok && r.data) this.dashboard = { ...this.dashboard, summary: r.data }
        else if (r.reason) this.loadError = loadErrorMessage(r.reason)
      } finally {
        this.isLoading = false
      }
    },

    /* ชุดใหญ่ — มี top_pages / top_properties / top_search_keywords มาให้ครบอยู่แล้ว */
    async fetchDashboard() {
      const base = this.requestBase()

      try {
        this.dashLoading = true

        const r = await settleOne(analyticsHttp.get('analytics/v1/dashboard', base))

        if (!r.ok) {
          this.loadError = loadErrorMessage(r.reason)
          this.dashboard = emptyDashboard()
          console.error('Error loading analytics dashboard:', r.reason)

          return
        }

        this.dashboard = { ...emptyDashboard(), ...(r.data || {}) }
        this.cached = r.cached
        this.applyDashboardPages()
      } finally {
        this.dashLoading = false
      }
    },

    /*
     * ── หน้าแรกของตารางที่แบ่งหน้า เอามาจาก /dashboard เลย ────────────────────
     *
     * /dashboard คำนวณ top_pages / top_properties / top_keywords มาให้อยู่แล้ว
     * แต่เดิมหน้าเว็บไม่ได้ใช้ กลับไปยิง /report แยกอีก 3 ครั้งด้วยเงื่อนไขเดียวกัน
     * (คนละ cache key -> DB คำนวณซ้ำจริง ๆ top_pages ตัวเดียวก็ 6 วินาทีแล้ว)
     *
     * ชุดใน dashboard เรียงด้วย ORDER BY เดียวกันและตัดมา 50 แถว (DEFAULT_PER_PAGE)
     * หน้าแรกที่ per_page 20 จึงเป็น 20 แถวแรกของชุดนั้นพอดี ส่วนหน้า 2 ขึ้นไป
     * ค่อยยิง /report แยกตามเดิม
     */
    applyDashboardPages() {
      const fromDashboard = {
        top_properties: this.dashboard.top_properties,
        top_search_keywords: this.dashboard.top_keywords,
        top_pages: this.dashboard.top_pages,
      }

      this.pagedKeys.forEach(type => {
        if (this.paged[type].page !== 1) return

        const all = Array.isArray(fromDashboard[type]) ? fromDashboard[type] : []

        this.paged[type].rows = all.slice(0, PAGED_PER_PAGE)
        this.paged[type].hasMore = all.length > PAGED_PER_PAGE
        this.paged[type].total = null
        this.paged[type].totalPages = null
      })
    },

    /* หน้า 2 ขึ้นไปของตารางเดียว — ยิงเฉพาะตัวนั้น ไม่ลากทั้งหน้ามาโหลดใหม่ */
    async fetchPaged(type) {
      try {
        this.$set(this.pagedLoading, type, true)

        const r = await settleOne(this.reportRequest(type, {
          page: this.paged[type].page,
          per_page: PAGED_PER_PAGE,
        }))

        this.paged[type].rows = rowsOf(r)
        this.paged[type].hasMore = r.hasMore
        this.paged[type].total = r.total
        this.paged[type].totalPages = r.totalPages
      } finally {
        this.$set(this.pagedLoading, type, false)
      }
    },

    /*
     * ── แท็บที่ต้องยิงแยก ───────────────────────────────────────────────────
     * utm / viewSources / geo ไม่ได้อยู่ใน /dashboard และเป็นสามตัวที่ช้าที่สุด
     * (17.6s / 7.8s / 22.4s ตอน cache ว่าง) ทั้งสามอยู่ในแท็บที่ไม่ได้เปิดค้างไว้
     * จึงเลื่อนไปโหลดตอนกดเข้าแท็บนั้นจริง ๆ
     */
    ensureTab(key) {
      if (this.lazy[key].loaded || this.lazy[key].loading) return

      this.fetchTab(key)
    },

    async fetchTab(key) {
      const jobs = {
        utm: () => this.reportRequest('utm_performance'),
        viewSources: () => this.reportRequest('view_sources'),
        geo: () => this.reportRequest('geo_detail', { page: this.geoPage, per_page: 15 }),
      }

      try {
        this.$set(this.lazy[key], 'loading', true)

        const r = await settleOne(jobs[key]())

        this[key] = rowsOf(r)
        if (key === 'geo') {
          this.geoHasMore = r.hasMore
          this.geoTotal = r.total
          this.geoTotalPages = r.totalPages
        }

        this.$set(this.lazy[key], 'loaded', true)
        this.$set(this.lazy[key], 'failed', !r.ok)

        /*
         * แท็บเดียวพังไม่ควรล้มทั้งหน้า แต่ต้องบอกให้รู้ ไม่งั้นจะดูเหมือน
         * "ไม่มีข้อมูล" ทั้งที่จริง ๆ คือเรียก API ไม่สำเร็จ
         */
        this.partialError = Object.keys(this.lazy)
          .filter(k => this.lazy[k].failed)
          .map(k => TAB_LABELS[k])
          .join(', ')
        if (this.partialError) this.partialError = `โหลดไม่ได้: ${this.partialError}`
      } finally {
        this.$set(this.lazy[key], 'loading', false)
      }
    },

    /* เปลี่ยนช่วงวันแล้ว แท็บที่เคยเปิดไว้ต้องโหลดใหม่ ส่วนที่ยังไม่เคยเปิดก็ยังไม่ต้อง */
    refreshLoadedTabs() {
      return Promise.all(
        Object.keys(this.lazy)
          .filter(key => this.lazy[key].loaded)
          .map(key => this.fetchTab(key)),
      )
    },

    pageLabel,
    hasNextPage,
    showPager,
    hasMultiplePages,

    /* ตัวห่อสำหรับตารางในกลุ่ม paged[] เพื่อไม่ต้องเขียน 3 อาร์กิวเมนต์ซ้ำในเทมเพลต */
    pagedLabel(type) {
      const p = this.paged[type]

      return pageLabel(p.page, p.totalPages, p.total)
    },
    pagedHasNext(type) {
      const p = this.paged[type]

      return hasNextPage(p.page, p.totalPages, p.hasMore)
    },
    pagedShowPager(type) {
      const p = this.paged[type]

      return showPager(p.page, p.totalPages, p.hasMore)
    },
    pagedHasMultiple(type) {
      const p = this.paged[type]

      return hasMultiplePages(p.page, p.totalPages, p.hasMore)
    },

    /*
     * เปลี่ยนหน้าของตารางเดียว — ยิงเฉพาะรายงานนั้น
     * เดิมเรียก fetchData() ซึ่งลากทั้ง /dashboard และทุกรายงานมาโหลดใหม่หมด
     * แค่กดปุ่ม "ถัดไป" ของตารางเดียว
     */
    goPagedPage(type, page) {
      this.paged[type].page = Math.max(1, page)

      /* กลับมาหน้าแรก = ชุดที่ /dashboard ส่งมาให้อยู่แล้ว ไม่ต้องยิงใหม่ */
      if (this.paged[type].page === 1) this.applyDashboardPages()
      else this.fetchPaged(type)
    },
    resetPages() {
      // เปลี่ยนช่วงเวลาแล้วต้องกลับหน้าแรก ไม่งั้นค้างอยู่หน้าที่อาจไม่มีข้อมูล
      this.pagedKeys.forEach(k => { this.paged[k].page = 1 })
    },
    goGeoPage(page) {
      this.geoPage = Math.max(1, page)
      this.fetchTab('geo')
    },
    onPresetChange() {
      if (!this.preset) return

      const today = new Date()

      this.to = iso(today)
      this.from = iso(new Date(today.getTime() - (this.preset - 1) * 86400000))
      this.geoPage = 1
      this.resetPages()
      this.fetchData()
    },
    onDateChange() {
      // แก้วันเองแล้ว preset ไม่ตรงกับช่วงอีกต่อไป
      this.preset = 0
      this.geoPage = 1
      this.resetPages()
      this.fetchData()
    },
    shareOf(cnt) {
      return this.totalConversions ? (100 * Number(cnt || 0)) / this.totalConversions : null
    },
    sourceLabel(src) {
      return {
        search_result: 'ผลการค้นหา',
        recommended: 'ห้องแนะนำ',
        similar: 'ห้องใกล้เคียง',
        available_units: 'ห้องว่างในโครงการ',
        recently_viewed: 'ดูล่าสุด',
        favorite: 'รายการโปรด',
        enquiry: 'หน้าสอบถาม',
        direct: 'เข้าตรง',
        other: 'อื่น ๆ',
      }[src] || src || '-'
    },
    sourceVariant(src) {
      if (src === 'search_result') return 'light-primary'
      if (src === 'direct') return 'light-secondary'

      return 'light-info'
    },
    channelLabel(type) {
      return CHANNEL_LABEL[type] || type || '-'
    },
    channelVariant(type) {
      if (type === 'line') return 'light-success'
      if (type === 'whatsapp') return 'light-success'
      if (type === 'phone') return 'light-primary'
      if (type === 'contact_form') return 'light-warning'

      return 'light-secondary'
    },
    donutOptions(labels) {
      return {
        ...APEX_BASE,
        labels,
        legend: { position: 'bottom' },
        dataLabels: { enabled: true, formatter: v => `${Number(v).toFixed(0)}%` },
        tooltip: { y: { formatter: v => `${Number(v).toLocaleString('en-US')}` } },
      }
    },

    /*
     * แท่งนอน (horizontal) ไม่ใช่แท่งตั้ง เพราะแกนเป็นคำค้นภาษาไทยที่ยาว
     * ถ้าเป็นแท่งตั้งชื่อจะเอียงทับกันจนอ่านไม่ออก
     */
    barChart(rows, labelKey, seriesName, color) {
      const top = (rows || []).slice(0, 10)

      return {
        series: [{ name: seriesName, data: top.map(r => Number(r.cnt || 0)) }],
        options: {
          ...APEX_BASE,
          chart: { ...APEX_BASE.chart, type: 'bar' },
          colors: [color],
          plotOptions: { bar: { horizontal: true, borderRadius: 3, barHeight: '70%' } },
          dataLabels: { enabled: true },
          xaxis: {
            categories: top.map(r => r[labelKey] || '(ว่าง)'),
            // จอแคบ: ตัดชื่อยาวไม่ให้กินพื้นที่จนแท่งเหลือนิดเดียว (ค่าเต็มยังอยู่ในตารางด้านล่าง)
            labels: { maxWidth: 160, style: { fontSize: '11px' } },
          },
          grid: { borderColor: 'rgba(0,0,0,.08)' },
        },
      }
    },

    /** สูงตามจำนวนแท่ง ไม่งั้นแท่งเยอะแล้วเบียดกันจนอ่านชื่อไม่ออก */
    barHeight(count) {
      return Math.max(180, 42 * (count || 1))
    },
    number(value) {
      const n = Number(value)

      return Number.isFinite(n) ? n.toLocaleString('en-US') : '—'
    },
    percent(value) {
      const n = Number(value)

      return Number.isFinite(n) ? `${n.toFixed(1)}%` : '—'
    },
    rate(part, whole) {
      const w = Number(whole || 0)

      return w ? (100 * Number(part || 0)) / w : null
    },
    money(value) {
      const n = Number(value)

      return Number.isFinite(n) && n ? n.toLocaleString('en-US') : '—'
    },
  },
}
</script>

<style scoped>
/*
 * แท็บเลื่อนแนวนอนบนจอเล็ก
 * ปกติ .nav-tabs จะ wrap ทำให้ 9 แท็บกลายเป็น 3-4 บรรทัด
 */
.analytics-tabs-nav :deep(.nav-tabs) {
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
}

.analytics-tabs-nav :deep(.nav-tabs)::-webkit-scrollbar {
  height: 4px;
}

.analytics-tabs-nav :deep(.nav-tabs)::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, .2);
  border-radius: 2px;
}

.analytics-tabs-nav :deep(.nav-item) {
  white-space: nowrap;
}

/*
 * URL / คำค้นยาว ๆ ดันตารางให้กว้างเกินจำเป็นจนต้องเลื่อนแนวนอนไกล
 * ยอมให้ตัดบรรทัดได้ ตารางจะแคบลงและอ่านง่ายขึ้นบนมือถือ
 */
:deep(.table td) {
  word-break: break-word;
}

/* ตัวเลขในการ์ดสรุปยาวเกินกรอบบนจอแคบ */
@media (max-width: 575.98px) {
  .card-body h4 {
    font-size: 1.15rem;
  }
}
</style>
